import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Video, Plus, X, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

interface TeamVideoUploadFormProps {
  isOpen: boolean;
  onClose: () => void;
  teamId: number;
  teamName: string;
  onUpdate: () => void;
}

interface VideoEntry {
  id: string;
  title: string;
  url: string;
  description?: string;
}

export const TeamVideoUploadForm = ({
  isOpen,
  onClose,
  teamId,
  teamName,
  onUpdate
}: TeamVideoUploadFormProps) => {
  const [uploading, setUploading] = useState(false);
  const [videos, setVideos] = useState<VideoEntry[]>([
    { id: '1', title: '', url: '', description: '' }
  ]);

  const addVideoEntry = () => {
    const newId = (videos.length + 1).toString();
    setVideos([...videos, { id: newId, title: '', url: '', description: '' }]);
  };

  const removeVideoEntry = (id: string) => {
    if (videos.length > 1) {
      setVideos(videos.filter(video => video.id !== id));
    }
  };

  const updateVideoEntry = (id: string, field: keyof VideoEntry, value: string) => {
    setVideos(videos.map(video => 
      video.id === id ? { ...video, [field]: value } : video
    ));
  };

  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const uploadVideos = async () => {
    const validVideos = videos.filter(video => video.title.trim() && video.url.trim());
    
    if (validVideos.length === 0) {
      toast.error('Please add at least one video with a title and URL');
      return;
    }

    // Validate URLs
    const invalidUrls = validVideos.filter(video => !isValidUrl(video.url));
    if (invalidUrls.length > 0) {
      toast.error('Please enter valid URLs for all videos');
      return;
    }

    try {
      setUploading(true);
      
      // Get current user ID for uploaded_by field - prioritize internal user
      let currentUserId: string | null = null;
      
      // First check internal_user_session which is where useInternalAuth stores the user
      try {
        const sessionData = localStorage.getItem('internal_user_session');
        if (sessionData) {
          console.log('Found internal_user_session:', sessionData);
          const parsed = JSON.parse(sessionData);
          if (parsed && parsed.user && parsed.user.id) {
            currentUserId = parsed.user.id;
            console.log('Using internal user ID from session for video upload:', currentUserId);
          }
        }
      } catch (e) {
        console.log('Error parsing internal_user_session:', e);
      }
      
      // If no internal user session, try Supabase auth
      if (!currentUserId) {
        const { data: { user } } = await supabase.auth.getUser();
        currentUserId = user?.id || null;
        if (currentUserId) {
          console.log('Using Supabase user ID for video upload:', currentUserId);
        }
      }

      if (!currentUserId) {
        toast.error('You must be logged in to add videos');
        return;
      }
      
      for (const video of validVideos) {
        const { error } = await supabase
          .from('team_videos')
          .insert({
            team_id: teamId,
            video_url: video.url,
            video_title: video.title,
            video_description: video.description || null,
            uploaded_by: currentUserId,
            display_order: 1
          });
        
        if (error) {
          console.error('Database error:', error);
          toast.error(`Failed to add video "${video.title}": ${error.message}`);
          continue;
        }
      }

      toast.success(`Successfully added ${validVideos.length} video(s)`);
      onUpdate();
      clearForm();
    } catch (error) {
      console.error('Error uploading videos:', error);
      toast.error('Failed to upload videos');
    } finally {
      setUploading(false);
    }
  };

  const clearForm = () => {
    setVideos([{ id: '1', title: '', url: '', description: '' }]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Video className="h-5 w-5" />
            Add Team Videos - {teamName}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Video Links</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addVideoEntry}
                  disabled={uploading}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Video
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {videos.map((video, index) => (
                <div key={video.id} className="p-4 border rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Video {index + 1}</h4>
                    {videos.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeVideoEntry(video.id)}
                        disabled={uploading}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    <div>
                      <Label htmlFor={`title-${video.id}`}>Video Title *</Label>
                      <Input
                        id={`title-${video.id}`}
                        value={video.title}
                        onChange={(e) => updateVideoEntry(video.id, 'title', e.target.value)}
                        placeholder="Enter video title..."
                        disabled={uploading}
                      />
                    </div>

                    <div>
                      <Label htmlFor={`url-${video.id}`}>Video URL *</Label>
                      <div className="relative">
                        <Input
                          id={`url-${video.id}`}
                          value={video.url}
                          onChange={(e) => updateVideoEntry(video.id, 'url', e.target.value)}
                          placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                          disabled={uploading}
                        />
                        {video.url && isValidUrl(video.url) && (
                          <ExternalLink className="absolute right-3 top-3 h-4 w-4 text-green-500" />
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor={`description-${video.id}`}>Description (optional)</Label>
                      <Textarea
                        id={`description-${video.id}`}
                        value={video.description}
                        onChange={(e) => updateVideoEntry(video.id, 'description', e.target.value)}
                        placeholder="Add a description for this video..."
                        disabled={uploading}
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex gap-2">
                <Button
                  onClick={uploadVideos}
                  disabled={uploading}
                  className="flex-1"
                >
                  {uploading ? 'Adding Videos...' : `Add ${videos.filter(v => v.title && v.url).length} Video(s)`}
                </Button>
                
                <Button
                  type="button"
                  variant="outline"
                  onClick={clearForm}
                  disabled={uploading}
                >
                  Clear All
                </Button>
              </div>

              <div className="text-xs text-muted-foreground space-y-1">
                <p>• Supported platforms: YouTube, Vimeo, and other video hosting services</p>
                <p>• Enter the full URL of the video (e.g., https://youtube.com/watch?v=...)</p>
                <p>• Both title and URL are required for each video</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};